import { useEffect } from "react";
import {
  MapPin,
  Phone,
  Mail,
  ChevronRight,
  Trophy,
  BookOpen,
  Award,
  Globe,
  Building2,
  Microscope,
  Music,
  Palette,
  Dumbbell,
  HeartHandshake,
  Quote,
  Lightbulb,
} from "lucide-react";

const GF =
  "https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700&family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400;1,600&family=EB+Garamond:ital,wght@0,400;1,400&display=swap";

const PILLARS = [
  {
    icon: BookOpen,
    name: "Academic Excellence",
    text: "Rigorous CBSE curriculum with innovative teaching methodologies and personalized learning paths.",
  },
  {
    icon: HeartHandshake,
    name: "Character Building",
    text: "Values-based education fostering integrity, empathy, and leadership in every student.",
  },
  {
    icon: Globe,
    name: "Global Perspective",
    text: "International exchange programs, global certifications, and multicultural awareness initiatives.",
  },
  {
    icon: Trophy,
    name: "Holistic Development",
    text: "State-level champions in sports, arts, and academic competitions with dedicated training facilities.",
  },
];

const STEPS = [
  {
    num: "01",
    name: "Online Application",
    desc: "Fill out the enquiry form with student details and required documents.",
  },
  {
    num: "02",
    name: "Entrance Assessment",
    desc: "Age-appropriate written test and interactive session for Grade III and above.",
  },
  {
    num: "03",
    name: "Family Interview",
    desc: "Personal interaction with the Principal to understand family values and expectations.",
  },
  {
    num: "04",
    name: "Admission Confirmation",
    desc: "Fee payment, document verification, and welcome to the school family.",
  },
];

const FACILITIES = [
  {
    icon: Building2,
    name: "Modern Infrastructure",
    desc: "Smart classrooms, science labs, computer centers, and digital library with 50,000+ resources.",
  },
  {
    icon: Microscope,
    name: "Science & Research",
    desc: "Advanced physics, chemistry, biology labs and robotics innovation center for hands-on learning.",
  },
  {
    icon: Music,
    name: "Performing Arts",
    desc: "Professional music rooms, dance studios, and 500-seat auditorium for cultural events.",
  },
  {
    icon: Palette,
    name: "Visual Arts",
    desc: "Art studios, pottery workshops, and exhibition spaces to nurture creative expression.",
  },
  {
    icon: Dumbbell,
    name: "Sports Complex",
    desc: "Olympic-size pool, indoor courts, cricket ground, and professional coaching staff.",
  },
  {
    icon: Lightbulb,
    name: "Innovation Hub",
    desc: "STEM lab, coding studios, 3D printing, and AI learning center for future-ready skills.",
  },
];

const TESTIMONIALS = [
  {
    name: "Dr. Priya Sharma",
    role: "Parent, Class of 2023",
    text: "The transformation in my daughter has been remarkable. From a shy introvert to a confident public speaker - this school nurtures potential we never knew existed.",
    image: "👩‍⚕️",
  },
  {
    name: "Rahul Verma",
    role: "Alumnus, IIT Delhi",
    text: "The foundation I received here was instrumental in my JEE success. The teachers went above and beyond, providing personal mentorship even after school hours.",
    image: "👨‍🎓",
  },
  {
    name: "Mrs. Kavita Rao",
    role: "Parent, Current Student",
    text: "What sets this school apart is the genuine care each teacher shows. My son looks forward to school every single day, which speaks volumes about the environment.",
    image: "👩‍💼",
  },
];

const ACHIEVEMENTS = [
  {
    year: "2024",
    title: "National Science Olympiad Winners",
    desc: "3 Gold medals and 2 Silver medals in the National Science Olympiad Championship.",
  },
  {
    year: "2024",
    title: "Best CBSE School Award",
    desc: "Recognized as the Best CBSE School in the region by Education Today Magazine.",
  },
  {
    year: "2023",
    title: "International Robotics Competition",
    desc: "First place in the International Robotics Challenge held in Singapore.",
  },
  {
    year: "2023",
    title: "100% Board Results",
    desc: "All students scored above 75% in CBSE Board examinations with 45% achieving 90%+.",
  },
];

const FAQS = [
  {
    q: "What is the admission process?",
    a: "Our admission process includes an online application, entrance assessment (for Grade III+), family interview, and document verification. We focus on holistic evaluation rather than just academic scores.",
  },
  {
    q: "What curriculum do you follow?",
    a: "We follow the CBSE curriculum enriched with global best practices, integrated technology, and emphasis on experiential learning.",
  },
  {
    q: "Do you provide transportation facilities?",
    a: "Yes, we have a fleet of 25 GPS-enabled buses covering all major routes within 15km radius of the school campus.",
  },
  {
    q: "What extracurricular activities are offered?",
    a: "We offer 20+ activities including sports (cricket, basketball, swimming, tennis), arts (music, dance, drama, painting), and clubs (robotics, coding, debate, MUN).",
  },
  {
    q: "Is there a daycare facility available?",
    a: "Yes, we provide extended daycare services until 6 PM with supervised homework time and recreational activities.",
  },
];

const NEWS_EVENTS = [
  {
    date: "15 Jan",
    month: "2025",
    type: "Event",
    title: "Annual Sports Day 2025",
    desc: "Join us for a day of athletic excellence and team spirit.",
  },
  {
    date: "22 Jan",
    month: "2025",
    type: "News",
    title: "New STEM Lab Inauguration",
    desc: "State-of-the-art robotics and AI learning center now open.",
  },
  {
    date: "01 Feb",
    month: "2025",
    type: "Event",
    title: "Founders Day Celebration",
    desc: "Commemorating 57 years of educational excellence.",
  },
];

export default function HeritageTheme({ school }) {
  const loginUrl = `/login?school=${school.slug}`;
  const age = new Date().getFullYear() - school.establishedYear;
  const initials = school.name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("");

  useEffect(() => {
    const link = Object.assign(document.createElement("link"), {
      rel: "stylesheet",
      href: GF,
    });
    document.head.appendChild(link);
    document.title = `${school.name} | EduPulse`;
    return () => document.head.removeChild(link);
  }, [school.name]);

  return (
    <div className="font-garamond bg-cream-warm text-navy overflow-x-hidden">
      {/* ── NAV ── */}
      <nav className="fixed top-0 inset-x-0 z-50 bg-navy border-b border-gold/20 h-[72px] flex items-center justify-between px-10">
        <div className="font-cinzel text-gold tracking-widest uppercase text-[15px] font-semibold leading-tight">
          {school.name}
          <span className="block text-[9px] text-gold-light tracking-[0.25em] font-normal mt-0.5 opacity-80">
            Est. {school.establishedYear} · {school.board}
          </span>
        </div>
        <ul className="hidden md:flex gap-9 list-none">
          {["About", "Academics", "Admissions", "Contact"].map((l) => (
            <li key={l}>
              <a
                href={`#${l.toLowerCase()}`}
                className="font-cinzel text-[11px] text-gold-light tracking-[0.18em] uppercase hover:text-gold transition-colors"
              >
                {l}
              </a>
            </li>
          ))}
        </ul>
        <a
          href={loginUrl}
          className="font-cinzel text-[10px] tracking-[0.15em] uppercase bg-gold text-navy px-6 py-2.5 hover:bg-gold-light transition-colors"
        >
          Student Login
        </a>
      </nav>

      {/* ── HERO ── */}
      <section className="min-h-screen bg-navy flex items-center relative overflow-hidden px-10 pt-[120px] pb-20">
        {/* radial glow */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_50%,rgba(201,168,76,0.08),transparent_60%)]" />
        {/* ornament ring */}
        <div className="hidden lg:flex absolute right-20 top-1/2 -translate-y-1/2 w-[420px] h-[420px] rounded-full border border-gold/15 items-center justify-center">
          <div className="absolute inset-6 rounded-full border border-gold/10" />
          <span className="font-cinzel text-[90px] text-gold/5 font-bold select-none">
            {initials}
          </span>
        </div>

        {/* content */}
        <div className="relative z-10 max-w-xl space-y-6">
          <div className="flex items-center gap-3 font-cinzel text-[10px] tracking-[0.3em] uppercase text-gold">
            <span className="w-10 h-px bg-gold/50" />
            Est. {school.establishedYear}
            <span className="w-10 h-px bg-gold/50" />
          </div>

          <h1 className="font-cormorant text-[clamp(48px,5vw,72px)] font-semibold leading-[1.08] text-cream">
            Shaping <em className="italic text-gold">Minds,</em>
            <br />
            Forging Character
          </h1>

          <p className="font-cormorant text-xl italic text-gold-light/80 leading-relaxed">
            {school.tagline}
          </p>
          <p className="text-[17px] text-cream/65 leading-[1.8]">
            For over {age} years, {school.name} has nurtured India's finest
            minds — instilling values of excellence, integrity, and service that
            endure a lifetime.
          </p>

          <div className="flex gap-5 items-center pt-2">
            <a
              href={loginUrl}
              className="font-cinzel text-[11px] tracking-[0.18em] uppercase bg-gold text-navy px-9 py-4 hover:bg-gold-light transition-colors"
            >
              Login to Portal
            </a>
            <a
              href="#about"
              className="font-cinzel text-[11px] tracking-[0.18em] uppercase text-gold border border-gold/40 px-9 py-4 hover:border-gold transition-colors"
            >
              Explore School
            </a>
          </div>
        </div>
      </section>

      {/* ── STATS STRIP ── */}
      <div className="bg-gold grid grid-cols-2 md:grid-cols-4 divide-x divide-navy/15">
        {[
          { num: `${age}`, label: "Years of Excellence" },
          { num: school.stats.students, label: "Current Students" },
          { num: school.stats.results, label: "Board Pass Rate" },
          { num: school.stats.alumni, label: "Distinguished Alumni" },
        ].map((s, i) => (
          <div key={i} className="flex flex-col items-center py-7 px-5">
            <span className="font-cinzel text-[34px] font-bold text-navy leading-none">
              {s.num}
            </span>
            <span className="font-cinzel text-[10px] tracking-[0.2em] uppercase text-navy/75 mt-1.5">
              {s.label}
            </span>
          </div>
        ))}
      </div>

      {/* ── ABOUT ── */}
      <section className="bg-cream py-24 px-10" id="about">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-20 items-center">
          {/* image placeholder */}
          <div className="relative">
            <div className="h-[460px] bg-navy flex items-center justify-center text-[100px] text-gold/15 relative overflow-hidden">
              🏛️
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_70%,rgba(201,168,76,0.06),transparent)]" />
              <div className="absolute bottom-0 left-0 right-0 bg-gold px-5 py-3 font-cinzel text-[11px] tracking-[0.15em] uppercase text-navy text-center">
                Main Campus — Founded {school.establishedYear}
              </div>
            </div>
          </div>
          <div>
            <div className="flex items-center gap-3 font-cinzel text-[10px] tracking-[0.3em] uppercase text-gold mb-4">
              Our Legacy <span className="w-10 h-px bg-gold/40" />
            </div>
            <h2 className="font-cormorant text-[clamp(36px,3.5vw,52px)] font-semibold leading-[1.15] text-navy mb-5">
              A Foundation Built on{" "}
              <em className="italic text-gold">Timeless Values</em>
            </h2>
            <p className="text-[17px] text-brown-muted leading-[1.8] mb-10">
              {school.name} has been a beacon of academic excellence and moral
              leadership since {school.establishedYear}. Our philosophy: educate
              the whole person — mind, character, and spirit — in alignment with{" "}
              {school.board} standards.
            </p>
            <div className="grid grid-cols-2 gap-5">
              {PILLARS.map(({ icon: Icon, name, text }) => (
                <div key={name} className="border-l-2 border-gold pl-5">
                  <div className="flex items-center gap-2 font-cinzel text-[12px] tracking-[0.1em] uppercase text-navy mb-1.5">
                    <Icon size={14} className="text-gold" />
                    {name}
                  </div>
                  <p className="text-[15px] text-brown-muted leading-relaxed">
                    {text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── STREAMS ── */}
      <section className="bg-cream-warm py-24 px-10" id="academics">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
            <div>
              <div className="flex items-center gap-3 font-cinzel text-[10px] tracking-[0.3em] uppercase text-gold mb-4">
                Programmes <span className="w-10 h-px bg-gold/40" />
              </div>
              <h2 className="font-cormorant text-[clamp(36px,3.5vw,52px)] font-semibold text-navy leading-tight">
                {school.streams.length}{" "}
                <em className="italic text-gold">Streams</em> of Excellence
              </h2>
            </div>
            <p className="text-[15px] text-brown-muted max-w-[280px] text-right leading-relaxed">
              Designed in alignment with {school.board} guidelines and
              university entrance requirements.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-0.5">
            {school.streams.map((stream, i) => (
              <div
                key={stream}
                className="bg-navy p-12 relative overflow-hidden group hover:-translate-y-1 transition-transform"
              >
                <div className="absolute top-0 left-0 right-0 h-[3px] bg-gold" />
                <div className="font-cinzel text-[48px] text-gold/10 font-bold leading-none mb-5">
                  0{i + 1}
                </div>
                <div className="font-cinzel text-[14px] tracking-[0.1em] uppercase text-gold mb-3">
                  {stream} Stream
                </div>
                <p className="text-[15px] text-cream/60 leading-relaxed">
                  Rigorous preparation with dedicated faculty, lab hours, and
                  holistic development for {school.board} examinations.
                </p>
                <ChevronRight
                  size={16}
                  className="text-gold/40 mt-6 group-hover:translate-x-1 transition-transform"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FACILITIES ── */}
      <section className="bg-cream py-24 px-10" id="facilities">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 font-cinzel text-[10px] tracking-[0.3em] uppercase text-gold mb-4">
              <span className="w-10 h-px bg-gold/40" />
              Campus Infrastructure
              <span className="w-10 h-px bg-gold/40" />
            </div>
            <h2 className="font-cormorant text-[clamp(36px,3.5vw,52px)] font-semibold text-navy leading-tight mb-4">
              World-Class <em className="italic text-gold">Facilities</em>
            </h2>
            <p className="text-[17px] text-brown-muted max-w-2xl mx-auto leading-relaxed">
              Our state-of-the-art campus provides everything students need to
              excel academically, artistically, and athletically.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FACILITIES.map(({ icon: Icon, name, desc }) => (
              <div
                key={name}
                className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gold/10"
              >
                <div className="w-14 h-14 bg-gold/10 rounded-lg flex items-center justify-center mb-5">
                  <Icon size={28} className="text-gold" />
                </div>
                <h3 className="font-cinzel text-[16px] font-semibold text-navy mb-3">
                  {name}
                </h3>
                <p className="text-[15px] text-brown-muted leading-relaxed">
                  {desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ACHIEVEMENTS ── */}
      <section className="bg-navy py-24 px-10" id="achievements">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 font-cinzel text-[10px] tracking-[0.3em] uppercase text-gold mb-4">
              <span className="w-10 h-px bg-gold/40" />
              Recognition & Awards
              <span className="w-10 h-px bg-gold/40" />
            </div>
            <h2 className="font-cormorant text-[clamp(36px,3.5vw,52px)] font-semibold text-cream leading-tight mb-4">
              Our <em className="italic text-gold">Achievements</em>
            </h2>
            <p className="text-[17px] text-cream/60 max-w-2xl mx-auto leading-relaxed">
              Celebrating excellence in academics, sports, and co-curricular
              activities.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {ACHIEVEMENTS.map((achievement, i) => (
              <div
                key={i}
                className="bg-cream/5 border border-gold/20 p-8 rounded-lg hover:bg-cream/10 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 bg-gold/20 rounded-full flex items-center justify-center shrink-0">
                    <Award size={28} className="text-gold" />
                  </div>
                  <div>
                    <span className="font-cinzel text-[12px] text-gold tracking-wider">
                      {achievement.year}
                    </span>
                    <h3 className="font-cormorant text-[22px] font-semibold text-cream mt-1 mb-2">
                      {achievement.title}
                    </h3>
                    <p className="text-[15px] text-cream/60 leading-relaxed">
                      {achievement.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="bg-cream-warm py-24 px-10" id="testimonials">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 font-cinzel text-[10px] tracking-[0.3em] uppercase text-gold mb-4">
              <span className="w-10 h-px bg-gold/40" />
              What Parents Say
              <span className="w-10 h-px bg-gold/40" />
            </div>
            <h2 className="font-cormorant text-[clamp(36px,3.5vw,52px)] font-semibold text-navy leading-tight">
              Words of <em className="italic text-gold">Appreciation</em>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                className="bg-white p-8 rounded-lg shadow-sm border border-gold/10"
              >
                <Quote size={32} className="text-gold/30 mb-6" />
                <p className="text-[16px] text-brown-muted leading-relaxed mb-6 italic">
                  "{t.text}"
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gold/10 rounded-full flex items-center justify-center text-2xl">
                    {t.image}
                  </div>
                  <div>
                    <div className="font-cinzel text-[14px] font-semibold text-navy">
                      {t.name}
                    </div>
                    <div className="text-[13px] text-brown-muted">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── NEWS & EVENTS ── */}
      <section className="bg-cream py-24 px-10" id="news">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
            <div>
              <div className="flex items-center gap-3 font-cinzel text-[10px] tracking-[0.3em] uppercase text-gold mb-4">
                Latest Updates <span className="w-10 h-px bg-gold/40" />
              </div>
              <h2 className="font-cormorant text-[clamp(36px,3.5vw,52px)] font-semibold text-navy leading-tight">
                News & <em className="italic text-gold">Events</em>
              </h2>
            </div>
            <a
              href="#news"
              className="text-[14px] text-gold hover:text-gold-light transition-colors flex items-center gap-2"
            >
              View All Updates <ChevronRight size={16} />
            </a>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {NEWS_EVENTS.map((item, i) => (
              <div
                key={i}
                className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow border border-gold/10"
              >
                <div className="bg-navy p-6 text-center">
                  <div className="font-cinzel text-[32px] font-bold text-gold">
                    {item.date}
                  </div>
                  <div className="font-cinzel text-[12px] text-cream/60 uppercase tracking-wider">
                    {item.month}
                  </div>
                </div>
                <div className="p-6">
                  <span
                    className={`inline-block font-cinzel text-[10px] px-3 py-1 rounded-full mb-3 ${
                      item.type === "Event"
                        ? "bg-gold/10 text-gold"
                        : "bg-navy/10 text-navy"
                    }`}
                  >
                    {item.type}
                  </span>
                  <h3 className="font-cormorant text-[20px] font-semibold text-navy mb-2">
                    {item.title}
                  </h3>
                  <p className="text-[14px] text-brown-muted leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="bg-cream-warm py-24 px-10" id="faq">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 font-cinzel text-[10px] tracking-[0.3em] uppercase text-gold mb-4">
              <span className="w-10 h-px bg-gold/40" />
              Common Questions
              <span className="w-10 h-px bg-gold/40" />
            </div>
            <h2 className="font-cormorant text-[clamp(36px,3.5vw,52px)] font-semibold text-navy leading-tight">
              Frequently Asked <em className="italic text-gold">Questions</em>
            </h2>
          </div>
          <div className="space-y-4">
            {FAQS.map((faq, i) => (
              <div
                key={i}
                className="bg-white rounded-lg border border-gold/10 overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-gold/10 rounded-full flex items-center justify-center shrink-0">
                      <span className="font-cinzel text-[14px] font-bold text-gold">
                        {i + 1}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-cinzel text-[16px] font-semibold text-navy mb-2">
                        {faq.q}
                      </h3>
                      <p className="text-[15px] text-brown-muted leading-relaxed">
                        {faq.a}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ADMISSIONS ── */}
      <section className="bg-navy py-24 px-10 text-center" id="admissions">
        <div className="flex items-center justify-center gap-3 font-cinzel text-[10px] tracking-[0.3em] uppercase text-gold mb-6">
          <span className="w-10 h-px bg-gold/50" />
          Admissions {new Date().getFullYear()}–
          {String(new Date().getFullYear() + 1).slice(2)}
          <span className="w-10 h-px bg-gold/50" />
        </div>
        <h2 className="font-cormorant text-[clamp(36px,3.5vw,52px)] font-semibold text-cream mb-4">
          Join the{" "}
          <em className="italic text-gold">{school.name.split(" ")[0]}</em>{" "}
          Family
        </h2>
        <p className="text-[17px] text-cream/50 max-w-xl mx-auto leading-[1.8] mb-16">
          Applications are open for Grades {school.academic?.grades?.[0]}{" "}
          through{" "}
          {school.academic?.grades?.[school.academic?.grades?.length - 1]}. Our
          admissions process is holistic — assessing academic potential and
          character.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 max-w-3xl mx-auto mb-16">
          {STEPS.map((s) => (
            <div key={s.num}>
              <div className="font-cinzel text-[48px] text-gold/15 font-bold leading-none">
                {s.num}
              </div>
              <div className="font-cinzel text-[11px] tracking-[0.15em] uppercase text-gold my-2">
                {s.name}
              </div>
              <p className="text-[14px] text-cream/50 leading-relaxed">
                {s.desc}
              </p>
            </div>
          ))}
        </div>
        <a
          href={loginUrl}
          className="inline-block font-cinzel text-[11px] tracking-[0.18em] uppercase bg-gold text-navy px-10 py-4 hover:bg-gold-light transition-colors"
        >
          Login to Portal
        </a>
      </section>

      {/* ── FOOTER ── */}
      <footer
        className="bg-navy border-t border-gold/20 py-16 px-10"
        id="contact"
      >
        <div className="max-w-6xl mx-auto grid md:grid-cols-4 gap-16 mb-12">
          <div className="md:col-span-1">
            <div className="font-cinzel text-[18px] text-gold font-semibold tracking-wide mb-2">
              {school.name}
            </div>
            <div className="font-cormorant italic text-[16px] text-gold-light/60 mb-5">
              {school.motto}
            </div>
            <div className="space-y-2 text-[14px] text-cream/40">
              <div className="flex items-start gap-2">
                <MapPin size={13} className="mt-0.5 shrink-0" />
                {school.address}
              </div>
              <div className="flex items-center gap-2">
                <Phone size={13} />
                {school.phone}
              </div>
              <div className="flex items-center gap-2">
                <Mail size={13} />
                {school.email}
              </div>
            </div>
          </div>
          {[
            {
              h: "Academics",
              links: [
                "Curriculum",
                "Timetable",
                "Examinations",
                "Results",
                "Scholarships",
              ],
            },
            {
              h: "Campus Life",
              links: ["Sports", "Arts & Music", "Clubs", "Events", "Gallery"],
            },
            {
              h: "Connect",
              links: [
                "Alumni Network",
                "Parent Portal",
                "Careers",
                "News",
                "Contact",
              ],
            },
          ].map((col) => (
            <div key={col.h}>
              <div className="font-cinzel text-[10px] tracking-[0.2em] uppercase text-gold mb-5">
                {col.h}
              </div>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l}>
                    <a
                      href="#contact"
                      className="text-[14px] text-cream/50 hover:text-gold transition-colors"
                    >
                      {l}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="border-t border-gold/10 pt-6 flex flex-col md:flex-row justify-between font-cinzel text-[10px] tracking-[0.15em] uppercase text-cream/25">
          <span>
            © {new Date().getFullYear()} {school.name}. All rights reserved.
          </span>
          <span>{school.affiliation}</span>
        </div>
      </footer>
    </div>
  );
}